import { Link, Navigate } from 'react-router-dom';
import { useRef, type ReactNode } from 'react';
import { ArrowRight, Bot, Globe, PieChart, Shield, Smartphone, Target } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { LandingHeader } from '@/components/LandingHeader';
import { LandingFooter } from '@/components/LandingFooter';
import { MvpBanner } from '@/components/MvpBanner';
import { useTranslation } from '@/hooks/useTranslation';
import { useAuth } from '@/contexts';
import { motion, useReducedMotion, useScroll, useSpring, useTransform } from 'framer-motion';
import {
  CountUp,
  EASE_OUT,
  Marquee,
  Reveal,
  ScrollProgress,
  SplitWords,
  SpotlightCard,
  Stagger,
  StaggerItem,
} from '@/components/landing/motion-primitives';
import { CtaSection, FaqItem, HeroBackdrop, SectionHeading } from '@/components/landing/page-kit';
import { ProductPreview } from '@/components/landing/ProductPreview';
import { StickyStory } from '@/components/landing/StickyStory';

/**
 * Landing page clevia.
 * Direction : une seule couleur d'accent (brand), typographie dominante, mouvement
 * au service de la lecture. Tout le mouvement se coupe sous `prefers-reduced-motion`.
 */

const BANKS = [
  ['cihbank_ma', 'CIH Bank'],
  ['attijariwafabank_com', 'Attijariwafa Bank'],
  ['bankofafrica_ma', 'Bank of Africa'],
  ['banquepopulaire_fr', 'Banque Populaire'],
  ['bnpparibasfortis_be', 'BNP Paribas'],
  ['credit-agricole_fr', 'Crédit Agricole'],
  ['creditmutuel_fr', 'Crédit Mutuel'],
  ['albaridbank_ma', 'Al Barid Bank'],
  ['belfius_be', 'Belfius'],
  ['cic_fr', 'CIC'],
  ['creditdumaroc_ma', 'Crédit du Maroc'],
  ['fortuneo_fr', 'Fortuneo'],
].map(([slug, label]) => ({ src: `/bank-logos/${slug}.png`, label }));

/* ── Mini visuel de la carte maîtresse ───────────────────────────── */

function FeatureHeroVisual({ label }: { label: string }) {
  const bars = [
    34, 41, 37, 45, 40, 52, 47, 58, 54, 49, 61, 57, 66, 62, 71, 67, 74, 70, 79, 75, 83, 88, 84, 92,
  ];

  return (
    <div className="mt-10 rounded-lg border border-border bg-muted/40 p-5">
      <div className="flex items-baseline justify-between">
        <span className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
          {label}
        </span>
        <span className="text-sm font-medium tabular-nums">
          <CountUp to={35450} /> €
        </span>
      </div>
      <div className="mt-4 flex h-16 items-end gap-1" aria-hidden="true">
        {bars.map((height, i) => (
          <motion.div
            key={i}
            initial={{ scaleY: 0 }}
            whileInView={{ scaleY: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.025, ease: EASE_OUT }}
            style={{ height: `${height}%`, transformOrigin: 'bottom' }}
            className={`flex-1 rounded-[2px] ${
              i >= bars.length - 3 ? 'bg-primary' : 'bg-primary/25'
            }`}
          />
        ))}
      </div>
    </div>
  );
}

/* ── Aperçu produit qui se redresse au scroll ────────────────────── */

function ShowcaseStage({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start 0.95', 'end 0.55'] });
  const progress = useSpring(scrollYProgress, { stiffness: 110, damping: 28, restDelta: 0.001 });

  const rotateX = useTransform(progress, [0, 1], [26, 0]);
  const scale = useTransform(progress, [0, 1], [0.9, 1]);
  const opacity = useTransform(progress, [0, 0.3], [0.65, 1]);

  if (reduce) {
    return <div ref={ref}>{children}</div>;
  }

  return (
    <div ref={ref} style={{ perspective: 1400 }}>
      <motion.div style={{ rotateX, scale, opacity, transformOrigin: 'top center' }}>
        {children}
      </motion.div>
    </div>
  );
}

/* ── Page ────────────────────────────────────────────────────────── */

export default function HomePage() {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, mfaPending } = useAuth();
  const stepsRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress: stepsProgress } = useScroll({
    target: stepsRef,
    offset: ['start 0.8', 'end 0.6'],
  });
  const lineScale = useSpring(stepsProgress, { stiffness: 100, damping: 30 });

  const heroFeature = {
    icon: PieChart,
    title: t('home.feature.tracking.title' as any),
    description: t('home.feature.tracking.desc' as any),
  };

  const features = [
    {
      icon: Shield,
      title: t('home.feature.security.title' as any),
      description: t('home.feature.security.desc' as any),
    },
    {
      icon: Bot,
      title: t('home.feature.ai.title' as any),
      description: t('home.feature.ai.desc' as any),
    },
    {
      icon: Target,
      title: t('home.feature.goals.title' as any),
      description: t('home.feature.goals.desc' as any),
    },
    {
      icon: Globe,
      title: t('home.feature.multi.title' as any),
      description: t('home.feature.multi.desc' as any),
    },
    {
      icon: Smartphone,
      title: t('home.feature.responsive.title' as any),
      description: t('home.feature.responsive.desc' as any),
    },
  ];

  const steps = [
    { number: '01', title: t('home.steps.1.title' as any), desc: t('home.steps.1.desc' as any) },
    { number: '02', title: t('home.steps.2.title' as any), desc: t('home.steps.2.desc' as any) },
    { number: '03', title: t('home.steps.3.title' as any), desc: t('home.steps.3.desc' as any) },
  ];

  const differentiators = [
    { title: t('home.whyDifferent.1.title' as any), desc: t('home.whyDifferent.1.desc' as any) },
    { title: t('home.whyDifferent.2.title' as any), desc: t('home.whyDifferent.2.desc' as any) },
    { title: t('home.whyDifferent.3.title' as any), desc: t('home.whyDifferent.3.desc' as any) },
    { title: t('home.whyDifferent.4.title' as any), desc: t('home.whyDifferent.4.desc' as any) },
  ];

  const faqs = [
    { q: t('home.faq.1.q' as any), a: t('home.faq.1.a' as any) },
    { q: t('home.faq.3.q' as any), a: t('home.faq.3.a' as any) },
    { q: t('home.faq.4.q' as any), a: t('home.faq.4.a' as any) },
    { q: t('home.faq.5.q' as any), a: t('home.faq.5.a' as any) },
    { q: t('home.faq.6.q' as any), a: t('home.faq.6.a' as any) },
  ];

  // Redirection des utilisateurs connectés (après tous les hooks)
  if (!isLoading && isAuthenticated && mfaPending) {
    return <Navigate to="/login" replace />;
  }
  if (!isLoading && isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <ScrollProgress />
      <MvpBanner />
      <LandingHeader />

      <main>
        {/* ── Hero ───────────────────────────────────────────────── */}
        <section className="relative px-6 pb-20 pt-28 lg:pb-28 lg:pt-40">
          <HeroBackdrop />

          <div className="mx-auto max-w-6xl">
            <h1 className="mx-auto max-w-4xl text-balance text-center text-4xl font-semibold leading-[1.06] tracking-tight sm:text-5xl lg:text-[4rem]">
              <SplitWords text={t('home.hero.title1' as any)} delay={0.1} />{' '}
              <SplitWords
                text={t('home.hero.title2' as any)}
                delay={0.1 + t('home.hero.title1' as any).split(' ').length * 0.055}
                wordClassName={() => 'text-brand-700 dark:text-brand-400'}
              />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.55, ease: EASE_OUT }}
              className="mx-auto mt-7 max-w-xl text-pretty text-center text-lg leading-relaxed text-muted-foreground"
            >
              {t('home.hero.description' as any)}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.68, ease: EASE_OUT }}
              className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row"
            >
              <Link to="/register" className="w-full sm:w-auto">
                <Button
                  size="lg"
                  className="group relative w-full gap-2 overflow-hidden px-8 shadow-lg shadow-brand-600/20 transition-transform duration-200 hover:-translate-y-0.5 sm:w-auto"
                >
                  <span className="relative z-10 flex items-center gap-2">
                    {t('home.cta.start' as any)}
                    <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </span>
                  <span
                    aria-hidden="true"
                    className="absolute inset-y-0 -left-full w-1/2 bg-white/25 blur-md motion-safe:group-hover:animate-shine"
                  />
                </Button>
              </Link>
              <Link
                to="/founders"
                className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-md border border-border px-7 text-sm font-medium transition-all duration-200 hover:-translate-y-0.5 hover:bg-muted sm:w-auto"
              >
                {t('home.cta.demo' as any)}
              </Link>
            </motion.div>

            <motion.ul
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.9 }}
              className="mt-8 flex flex-wrap items-center justify-center gap-x-3 gap-y-2 text-sm text-muted-foreground"
            >
              <li>{t('home.trust.encrypted' as any)}</li>
              <li className="h-1 w-1 rounded-full bg-border" aria-hidden="true" />
              <li>{t('home.trust.free' as any)}</li>
              <li className="h-1 w-1 rounded-full bg-border" aria-hidden="true" />
              <li>{t('home.trust.eu' as any)}</li>
            </motion.ul>

            {/* Aperçu produit */}
            <div className="mt-20 lg:mt-24">
              <ShowcaseStage>
                <ProductPreview t={t} />
              </ShowcaseStage>
              <p className="mt-5 text-center text-sm text-muted-foreground">
                {t('home.mock.caption' as any)}
              </p>
            </div>
          </div>
        </section>

        {/* ── Banques ────────────────────────────────────────────── */}
        <section className="border-y border-border bg-muted/30 py-12">
          <div className="mx-auto max-w-6xl px-6">
            <Reveal>
              <p className="mb-8 text-center text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
                {t('home.banks.eyebrow' as any)}
              </p>
            </Reveal>
            <Marquee items={BANKS} />
          </div>
        </section>

        {/* ── Récit produit (sticky) ─────────────────────────────── */}
        <section className="border-b border-border px-6 py-24 lg:py-32">
          <div className="mx-auto max-w-6xl">
            <SectionHeading
              title={t('home.whatIs.title' as any)}
              subtitle={t('home.whatIs.intro' as any)}
            />

            <div className="mt-16 lg:mt-20">
              <StickyStory t={t} />
            </div>
          </div>
        </section>

        {/* ── Fonctionnalités (bento + spotlight) ────────────────── */}
        <section className="border-b border-border px-6 py-24 lg:py-32">
          <div className="mx-auto max-w-6xl">
            <SectionHeading
              eyebrow={t('home.section.features' as any)}
              title={t('home.features.title' as any)}
              subtitle={t('home.features.subtitle' as any)}
            />

            <Stagger className="mt-16 grid gap-4 md:grid-cols-2 lg:grid-cols-3" stagger={0.07}>
              {/* Carte maîtresse : 2 colonnes × 2 rangées */}
              <StaggerItem className="md:col-span-2 md:row-span-2">
                <SpotlightCard className="flex h-full flex-col justify-between rounded-xl border border-border bg-card p-8 transition-shadow duration-300 hover:shadow-lg">
                  <div>
                    <heroFeature.icon className="h-5 w-5 text-primary" />
                    <h3 className="mt-5 text-balance text-xl font-medium sm:text-2xl">
                      {heroFeature.title}
                    </h3>
                    <p className="mt-3 max-w-md text-pretty leading-relaxed text-muted-foreground">
                      {heroFeature.description}
                    </p>
                  </div>
                  <FeatureHeroVisual label={t('home.mock.totalWealth' as any)} />
                </SpotlightCard>
              </StaggerItem>

              {features.map((feature) => (
                <StaggerItem key={feature.title}>
                  <SpotlightCard className="h-full rounded-xl border border-border bg-card p-7 transition-shadow duration-300 hover:shadow-lg">
                    <feature.icon className="h-5 w-5 text-primary" />
                    <h3 className="mt-5 text-balance text-base font-medium">{feature.title}</h3>
                    <p className="mt-2 text-pretty text-sm leading-relaxed text-muted-foreground">
                      {feature.description}
                    </p>
                  </SpotlightCard>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>

        {/* ── Étapes ─────────────────────────────────────────────── */}
        <section className="border-b border-border px-6 py-24 lg:py-32">
          <div className="mx-auto max-w-6xl">
            <SectionHeading
              title={t('home.howItWorks.title' as any)}
              subtitle={t('home.howItWorks.subtitle' as any)}
              align="center"
            />

            <div ref={stepsRef} className="relative mt-20">
              <div
                className="absolute left-0 right-0 top-[7px] hidden h-px bg-border sm:block"
                aria-hidden="true"
              />
              <motion.div
                style={{ scaleX: lineScale }}
                className="absolute left-0 right-0 top-[7px] hidden h-px origin-left bg-primary sm:block"
                aria-hidden="true"
              />

              <Stagger className="grid gap-12 sm:grid-cols-3 sm:gap-10" stagger={0.12}>
                {steps.map((step) => (
                  <StaggerItem key={step.number}>
                    <span className="mb-8 hidden h-3.5 w-3.5 rounded-full border-2 border-primary bg-background sm:block" />
                    <span className="text-sm font-medium tabular-nums text-brand-700 dark:text-brand-400">
                      {step.number}
                    </span>
                    <h3 className="mt-3 text-balance text-lg font-medium sm:min-h-[3.5rem]">
                      {step.title}
                    </h3>
                    <p className="mt-2 text-pretty text-sm leading-relaxed text-muted-foreground">
                      {step.desc}
                    </p>
                  </StaggerItem>
                ))}
              </Stagger>
            </div>
          </div>
        </section>

        {/* ── Pourquoi clevia est différente ─────────────────────── */}
        <section className="relative overflow-hidden border-y border-white/10 bg-gray-950 px-6 py-24 dark:bg-white/[0.03] lg:py-32">
          <div
            className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#16c47f]/50 to-transparent"
            aria-hidden="true"
          />
          <div className="mx-auto grid max-w-6xl gap-14 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-20">
            <div>
              <SectionHeading
                eyebrow={t('home.section.different' as any)}
                title={t('home.whyDifferent.title' as any)}
                subtitle={t('home.whyDifferent.subtitle' as any)}
                tone="dark"
              />
              <Reveal delay={0.18}>
                <Link
                  to="/founders"
                  className="group mt-8 inline-flex items-center gap-2 text-sm font-medium text-[#16c47f]"
                >
                  {t('home.whyDifferent.cta' as any)}
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </Reveal>
            </div>

            <Stagger className="grid gap-x-10 gap-y-10 sm:grid-cols-2" stagger={0.09}>
              {differentiators.map((item) => (
                <StaggerItem key={item.title}>
                  <div className="border-t border-white/10 pt-6 transition-colors duration-300 hover:border-[#16c47f]/50">
                    <h3 className="text-balance text-base font-medium text-white">{item.title}</h3>
                    <p className="mt-2 text-pretty text-sm leading-relaxed text-gray-400">
                      {item.desc}
                    </p>
                  </div>
                </StaggerItem>
              ))}
            </Stagger>
          </div>
        </section>

        {/* ── FAQ ────────────────────────────────────────────────── */}
        <section className="border-b border-border px-6 py-24 lg:py-32">
          <div className="mx-auto grid max-w-6xl gap-14 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-20">
            <div>
              <div className="lg:sticky lg:top-28">
                <SectionHeading
                  eyebrow={t('home.section.faq' as any)}
                  title={t('home.faq.title' as any)}
                  subtitle={t('home.faq.subtitle' as any)}
                />
              </div>
            </div>

            <Stagger className="border-t border-border" stagger={0.06}>
              {faqs.map((faq) => (
                <FaqItem key={faq.q} question={faq.q} answer={faq.a} />
              ))}
            </Stagger>
          </div>
        </section>

        {/* ── CTA final ──────────────────────────────────────────── */}
        <CtaSection
          title={t('home.cta.final.title' as any)}
          subtitle={t('home.cta.final.subtitle' as any)}
          action={{ label: t('home.cta.final.button' as any), to: '/register' }}
          note={`${t('home.trust.encrypted' as any)} · ${t('home.trust.free' as any)}`}
        />
      </main>

      <LandingFooter />
    </div>
  );
}
